'use client'
import Image from 'next/image'
import * as prismic from '@prismicio/client'

import { cn } from '@/utils/twMerge'
import type { HeroProps } from '@/types/common'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicLink, PrismicRichText } from '@prismicio/react'
import { getHomeHeroHeader, getHomeHeroImage } from '@/utils/getHomeHeroData'

// Simple components don't need memo
const ProductHuntBadge = () => (
  <PrismicLink
    href="https://www.producthunt.com/posts/secureframe?utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-secureframe"
    target="_blank"
    aria-label="Opens in a new tab"
    className="mb-8 flex items-center justify-center">
    <Image
      src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=272363&theme=light&period=daily"
      alt="Secureframe - Get enterprise ready with SOC 2 and ISO 27001 compliance | Product Hunt"
      width={250}
      height={54}
    />
  </PrismicLink>
)

const MasterCardIcon = () => (
  <div className="relative h-[50px] w-[50px]">
    <Image
      alt="Mastercard offer icon"
      src="https://images.prismic.io/secureframe-com/68c846a2-6bb7-47d5-baab-dd0e3ef88783_+mastercard.png?auto=compress,format"
      fill
      className="object-contain"
      quality={100}
    />
  </div>
)

export const Hero = ({ data, uid, type, offerData }: HeroProps) => {
  const heroHeader = getHomeHeroHeader(uid as string, data, offerData)
  const heroImage = getHomeHeroImage(uid as string, data, offerData)
  const offerName = prismic.asText(offerData?.offer_name ?? []).trim()
  const offerText = prismic.asText(offerData?.offer_text ?? []).trim()

  return (
    <section className={cn('relative z-[1] pt-[42px] text-center lg:min-h-screen', uid ?? '')}>
      <div className="absolute right-0 top-0 -z-[1] h-auto w-full">
        <Image
          src="/images/hero-bg.svg"
          alt=""
          aria-hidden="true"
          width={1440}
          height={800}
          loading="eager"
          fetchPriority="high"
          quality={90}
          className="h-[750px] w-full object-cover sm:h-[550px] md:h-[600px] lg:h-auto"
          sizes="100vw"
        />
      </div>

      <div className="container">
        <div className="[&>h1]:em:not-italic [&>h1]:mb-8 [&>h1]:mt-0 [&>h1]:overflow-visible [&>h1]:bg-gradient-to-r [&>h1]:from-primary-ink-100 [&>h1]:to-primary-green-85 [&>h1]:bg-clip-text [&>h1]:pb-[0.12em] [&>h1]:font-normal [&>h1]:leading-[1.1] [&>h1]:tracking-[-0.84px] [&>h1]:text-transparent [&>h1]:lg:my-10 [&>h1]:lg:leading-[80px]">
          <PrismicRichText field={heroHeader} />
        </div>

        <div className="[&>p]:mb-10 [&>p]:font-halyardText [&>p]:text-[22px] [&>p]:font-normal [&>p]:leading-[140%] [&>p]:tracking-[0.22px] [&>p]:text-primary-ink-85">
          <PrismicRichText field={data.hero_description} />
        </div>

        {type === 'offer' && (
          <>
            {uid === 'producthunt' && <ProductHuntBadge />}

            <div className="mb-8 flex items-center justify-center gap-4">
              {uid === 'mastercard-easy-savings' && <MasterCardIcon />}

              <p className="font-halyardDisplay text-xl">{[offerName, offerText].filter(Boolean).join(' ')}</p>
            </div>
          </>
        )}

        <PrismicLink field={data.hero_btn_link} className="btn btn-primary btn-default btn-interaction">
          {data.hero_btn_label}
          <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
        </PrismicLink>

        <PrismicNextImage
          field={heroImage}
          width={1200}
          height={675}
          loading="eager"
          fetchPriority="high"
          className={cn(
            '-mb-[200px] mt-[40px] hidden w-full lg:block',
            uid === 'mastercard-easy-savings' ? 'lg:mx-auto lg:max-w-xl' : '',
          )}
          sizes="(max-width: 640px) 640px, (max-width: 1024px) 1024px, 1200px"
          quality={90}
          placeholder="blur"
          blurDataURL={`data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx0fHRsdHSIeHx8dIiQjJCUkIyQkJSstMDYxLS0lJSkpKS4vMTM3ODg4JSk5OjspOi//2wBDAhUXFyIeIh0iIh0lJSUlLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS3/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k=`}
          fallbackAlt=""
        />
      </div>
    </section>
  )
}
